import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, MicOff, Square, Pause, Play, Loader2, GripHorizontal, Minimize2, Maximize2, X } from 'lucide-react';
import { Button } from '../ui/button';

const FloatingDictation = ({ recording, isGenerating, forceSingleColumn = false, textSizeLevel = 0 }) => {
  const [position, setPosition] = useState({ x: 20, y: 80 });
  const [isDragging, setIsDragging] = useState(false);
  const [hasUserDragged, setHasUserDragged] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  // Auto-dock to the right-margin gutter when 1-column view is forced. The
  // single-column container is centered with a maxWidth that scales with
  // textSizeLevel (800/960/1120 px), leaving symmetric empty gutters on the
  // left and right of the viewport. Parking the ribbon in that empty
  // right-side gutter keeps it visible without ever covering the centered
  // content / menu / Next Patient button.
  //
  // The user can still drag it manually — once they do, we respect their
  // chosen position and stop auto-docking for the rest of the session.
  useEffect(() => {
    if (hasUserDragged) return;
    if (!forceSingleColumn) {
      // 2-column mode — keep the default top-left anchor unless dragged
      setPosition({ x: 20, y: 80 });
      return;
    }
    const colMax = textSizeLevel >= 2 ? 1120 : textSizeLevel === 1 ? 960 : 800;
    const recompute = () => {
      const vw = window.innerWidth;
      const RIBBON_W = 200;        // approx width of the ribbon
      const GUTTER_PAD = 16;       // breathing room from edges/column
      // Center of the column = vw/2; column right edge = vw/2 + colMax/2.
      // Park ribbon between column-right-edge and viewport right edge.
      const colRightEdge = vw / 2 + colMax / 2;
      let x = colRightEdge + GUTTER_PAD;
      // If viewport is smaller than column+ribbon (narrow screen), fall
      // back to top-right corner with a small inset.
      if (x + RIBBON_W + GUTTER_PAD > vw) {
        x = Math.max(GUTTER_PAD, vw - RIBBON_W - GUTTER_PAD);
      }
      setPosition({ x, y: 88 });
    };
    recompute();
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, [forceSingleColumn, textSizeLevel, hasUserDragged]);

  // Safety timeout — if isTranscribing stays true for > 90s (LLM stalled
  // or audio API hung), automatically force-cancel so the ribbon doesn't
  // sit on screen forever blocking the Next Patient button.
  useEffect(() => {
    if (!recording.isTranscribing) return;
    const t = setTimeout(() => {
      if (recording.forceCancel) {
        recording.forceCancel();
        try { (window).__wr_toast_err && (window).__wr_toast_err('Transcription timed out — please re-record'); } catch { /* ignore */ }
      }
    }, 90_000);
    return () => clearTimeout(t);
  }, [recording.isTranscribing]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePointerDown = useCallback((e) => {
    if (e.target.closest('[data-no-drag]')) return;
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const handlePointerMove = useCallback((e) => {
    if (!isDragging) return;
    if (!hasUserDragged) setHasUserDragged(true);
    setPosition({
      x: Math.max(0, Math.min(window.innerWidth - 160, e.clientX - dragOffset.current.x)),
      y: Math.max(0, Math.min(window.innerHeight - 60, e.clientY - dragOffset.current.y)),
    });
  }, [isDragging, hasUserDragged]);

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Only show when recording or transcribing
  if (!recording.isRecording && !recording.isTranscribing) return null;

  return (
    <div
      className="fixed z-[9999] select-none"
      style={{ left: position.x, top: position.y, touchAction: 'none' }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      data-testid="floating-dictation"
    >
      <div className={`rounded-xl border shadow-2xl backdrop-blur-xl transition-all ${
        recording.isRecording 
          ? recording.isPaused 
            ? 'bg-amber-950/90 border-amber-500/50' 
            : 'bg-red-950/90 border-red-500/50' 
          : 'bg-blue-950/90 border-blue-500/50'
      }`}>
        {/* Drag handle */}
        <div className="flex items-center justify-between px-2 py-1 border-b border-white/10 cursor-grab active:cursor-grabbing">
          <GripHorizontal className="w-3.5 h-3.5 text-white/40" />
          <span className="text-[9px] text-white/50 font-medium uppercase tracking-wider mx-2">Dictation</span>
          <div className="flex items-center gap-0.5">
            <button data-no-drag onClick={() => setMinimized(!minimized)} className="p-0.5 text-white/40 hover:text-white/80" title={minimized ? 'Expand' : 'Minimize'}>
              {minimized ? <Maximize2 className="w-3 h-3" /> : <Minimize2 className="w-3 h-3" />}
            </button>
            <button
              data-no-drag
              onClick={(e) => {
                e.stopPropagation();
                if (recording.forceCancel) recording.forceCancel();
              }}
              className="p-0.5 text-red-300/80 hover:text-red-200 hover:bg-red-500/30 rounded-sm"
              title="Force close dictation ribbon"
              data-testid="float-force-close"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {!minimized && (
          <div className="p-2 flex items-center gap-2">
            {/* Recording indicator */}
            <div className="flex items-center gap-1.5">
              <span className={`w-2.5 h-2.5 rounded-full ${
                recording.isTranscribing ? 'bg-blue-400 animate-pulse' :
                recording.isPaused ? 'bg-amber-400' : 'bg-red-500 animate-pulse'
              }`} />
              <span className="text-xs font-bold text-white/90">
                {recording.isTranscribing ? 'Processing...' :
                 recording.isPaused ? 'PAUSED' : recording.recordingDurationFormatted || '0:00'}
              </span>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-1 ml-1" data-no-drag>
              {recording.isRecording && !recording.isTranscribing && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-white/80 hover:text-white hover:bg-white/10"
                    onClick={recording.isPaused ? recording.resumeRecording : recording.pauseRecording}
                    data-testid="float-pause-resume"
                  >
                    {recording.isPaused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                    onClick={recording.stopRecording}
                    data-testid="float-stop"
                  >
                    <Square className="w-3.5 h-3.5" />
                  </Button>
                </>
              )}
              {recording.isTranscribing && (
                <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
              )}
            </div>

            {/* Audio level bar */}
            {recording.isRecording && !recording.isPaused && !recording.isTranscribing && (
              <div className="w-12 h-1.5 bg-white/10 rounded-full overflow-hidden ml-1">
                <div
                  className="h-full bg-red-400 rounded-full transition-all duration-100"
                  style={{ width: `${Math.min(100, (recording.audioLevel || 0) * 100)}%` }}
                />
              </div>
            )}
          </div>
        )}

        {/* Minimized: just the dot and timer */}
        {minimized && (
          <div className="px-2 py-1 flex items-center gap-1.5" data-no-drag>
            <span className={`w-2 h-2 rounded-full ${
              recording.isTranscribing ? 'bg-blue-400 animate-pulse' :
              recording.isPaused ? 'bg-amber-400' : 'bg-red-500 animate-pulse'
            }`} />
            <span className="text-[10px] font-bold text-white/80">
              {recording.isTranscribing ? '...' : recording.recordingDurationFormatted || '0:00'}
            </span>
            {recording.isRecording && !recording.isTranscribing && (
              <button data-no-drag onClick={recording.stopRecording} className="p-0.5 text-red-400 hover:text-red-300">
                <Square className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FloatingDictation;
